import { useState, useEffect, useCallback, useRef } from "react";

import { API } from "../config";

/**
 * Hook pour gérer l'affichage des messages de motivation quotidiens
 * Affiche jusqu'à 2 messages par jour à des moments aléatoires
 */
export function useMotivationMessages(user) {
  const [showMotivation, setShowMotivation] = useState(false);
  const [motivationMessage, setMotivationMessage] = useState("");
  const isCheckingRef = useRef(false);

  // Vérifier si on doit afficher un message de motivation
  const checkMotivation = useCallback(async () => {
    if (!user || isCheckingRef.current) return;
    
    isCheckingRef.current = true;
    try {
      const token = localStorage.getItem("session_token");
      if (!token) return;
      
      // D'abord vérifier si c'est le bon moment pour afficher
      const shouldShowResponse = await fetch(`${API}/motivation/should-show`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (!shouldShowResponse.ok) return;
      
      const { should_show } = await shouldShowResponse.json();
      
      if (!should_show) return;
      
      // Si oui, récupérer le message
      const messageResponse = await fetch(`${API}/motivation/message`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (messageResponse.ok) {
        const data = await messageResponse.json();
        if (data.show_message && data.message) {
          setMotivationMessage(data.message);
          setShowMotivation(true);
        }
      }
    } catch (error) {
      // Silently fail - motivation messages are not critical
      console.log("Motivation check skipped");
    } finally {
      isCheckingRef.current = false;
    }
  }, [user]);

  // Vérifier à l'ouverture de l'app et périodiquement
  useEffect(() => {
    if (!user) return;
    
    // Vérifier après 10 secondes de chargement initial (pour ne pas bloquer l'auth)
    const initialTimer = setTimeout(() => {
      checkMotivation();
    }, 10000);
    
    // Vérifier toutes les 30 minutes
    const interval = setInterval(() => {
      checkMotivation();
    }, 30 * 60 * 1000);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [user, checkMotivation]);

  const closeMotivation = useCallback(() => {
    setShowMotivation(false);
    setMotivationMessage("");
  }, []);

  return {
    showMotivation,
    motivationMessage,
    closeMotivation,
    checkMotivation
  };
}
